import { useState } from "react";
import { X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SearchPick } from "@/components/SearchPick";
import { api } from "@/lib/api";
import { KIND } from "./TopFive";

const blank = { kind: "country", title: "", subtitle: "", note: "", add: null };
const BEEN = { hint: "Been there", hintColor: "#34D399" };
const NEW = { hint: "Will be added", hintColor: "#F59E0B" };
const MEANT = { hint: "Did you mean?", hintColor: "#7DD3FC" };

const Picked = ({ f, onClear }) => (
  <div className="flex items-center justify-between rounded-lg border border-white/10 bg-white/5 px-3 py-2" data-testid="top5-picked">
    <div className="min-w-0">
      <p className="text-sm font-medium truncate">{f.title}{f.subtitle && <span className="text-slate-400 font-normal">, {f.subtitle}</span>}</p>
      {f.add && <p className="text-[11px] text-amber-300">Not on your list yet — it will be added as visited</p>}
    </div>
    <button type="button" onClick={onClear} className="text-slate-400 hover:text-white" aria-label="Clear" data-testid="top5-picked-clear"><X size={14} /></button>
  </div>
);

export const TopFiveDialog = ({ data, b, names, onAdd, extra, children }) => {
  const taken = new Set((data.top5 || []).map((t) => `${t.kind}|${t.title}|${t.subtitle}`));
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(blank);
  const visitedSet = new Set(data.visited.map((v) => v.country));
  const beenCity = new Set(data.visited.flatMap((v) => v.cities.map((c) => `${c}|${v.country}`)));

  const countryOptions = [...names].sort((a, c) => visitedSet.has(c) - visitedSet.has(a))
    .filter((c) => !taken.has(`country|${c}|`))
    .map((c) => ({ key: c, label: c, ...(visitedSet.has(c) ? BEEN : NEW) }));
  const cityOptions = data.visited.flatMap((v) => v.cities.map((c) => ({ key: `${c}|${v.country}`, label: `${c}, ${v.country}`, value: c, city: c, country: v.country, ...BEEN })))
    .filter((o) => !taken.has(`city|${o.city}|${o.country}`));
  const remoteCities = (q) => api.searchCities("", q).then((r) => r.map((c) => ({
    key: `${c.name}|${c.country}|${c.x}`, label: c.region ? `${c.name}, ${c.region}, ${c.country}` : `${c.name}, ${c.country}`, value: c.name, city: c.name, country: c.country, place: { name: c.name, x: c.x, y: c.y },
    ...(beenCity.has(`${c.name}|${c.country}`) ? BEEN : c.fuzzy ? MEANT : c.kind ? { hint: c.kind, hintColor: "#94A3B8" } : NEW),
  })));
  const momentOptions = data.visited.flatMap((v) => (v.trips || []).filter((t) => t.note).map((t) => ({ key: `${t.note}|${v.country}`, label: t.note, value: t.note, sub: `${v.country} · ${t.date.slice(0, 4)}`, hint: v.country })))
    .concat(data.goals.filter((g) => g.done).map((g) => ({ key: `goal-${g.id}`, label: g.title, value: g.title, sub: g.completed_at?.slice(0, 4) || "", hint: "Dream done" })));

  const pickCountry = (o) => setF({ ...f, title: o.label, subtitle: "", add: visitedSet.has(o.label) ? null : { country: o.label } });
  const pickCity = (o) => setF({ ...f, title: o.city, subtitle: o.country, add: beenCity.has(`${o.city}|${o.country}`) ? null : { country: o.country, city: o.city, place: o.place } });
  const createCity = (name) => setF({ ...f, title: name, subtitle: "", add: { city: name, country: "" } });
  const cityCountry = (o) => setF({ ...f, subtitle: o.label, add: { ...f.add, country: o.label } });

  const onOpen = (o) => { if (o) setF(blank); setOpen(o); };
  const ready = f.title.trim() && (f.kind !== "city" || f.subtitle);
  const submit = async (e) => {
    e.preventDefault();
    if (!ready) return;
    if (f.add?.city) { if (!beenCity.has(`${f.add.city}|${f.add.country}`)) b.toggleCity(f.add.country, f.add.city, f.add.place); }
    else if (f.add?.country && !visitedSet.has(f.add.country)) b.toggleCountry(f.add.country);
    await onAdd({ kind: f.kind, title: f.title.trim(), subtitle: f.subtitle.trim(), note: f.note.trim() });
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="max-w-md" data-testid="top5-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{extra ? "Add an extra" : "Add to your top 5"}</DialogTitle>
          <DialogDescription>Type to search. If it's not on your list yet, it gets added.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-3 gap-2">
            {Object.entries(KIND).map(([k, K]) => (
              <button type="button" key={k} onClick={() => setF({ ...blank, kind: k })} data-testid={`top5-kind-${k}`}
                className="flex items-center justify-center gap-1.5 py-2 rounded-lg border text-sm transition-colors"
                style={f.kind === k ? { borderColor: K.color, color: K.color, background: `${K.color}14` } : { borderColor: "rgba(255,255,255,0.1)", color: "#94A3B8" }}>
                <K.icon size={14} /> {K.label}
              </button>
            ))}
          </div>
          {f.kind === "country" && (f.title ? <Picked f={f} onClear={() => setF({ ...f, title: "", add: null })} /> : (
            <SearchPick options={countryOptions} onPick={pickCountry} placeholder="Search any country…" testId="top5-country-search" />
          ))}
          {f.kind === "city" && (f.title ? (
            <>
              <Picked f={f} onClear={() => setF({ ...f, title: "", subtitle: "", add: null })} />
              {!f.subtitle && <SearchPick options={names.map((c) => ({ key: c, label: c }))} onPick={cityCountry} placeholder={`Which country is ${f.title} in?`} testId="top5-city-country-search" />}
            </>
          ) : (
            <SearchPick options={cityOptions} remote={remoteCities} onPick={pickCity} onCreate={createCity} createLabel={(q) => `Add "${q}" as a city`} placeholder="Search any city…" testId="top5-city-search" accent="#39FF88" />
          ))}
          {f.kind === "event" && (
            <>
              {f.title ? <Picked f={f} onClear={() => setF({ ...f, title: "", subtitle: "" })} /> : (
                <SearchPick options={momentOptions} onPick={(o) => setF({ ...f, title: o.value, subtitle: o.sub })} onCreate={(q) => setF({ ...f, title: q })}
                  createLabel={(q) => `Add "${q}" as a moment`} placeholder="e.g. Northern lights in Tromsø" testId="top5-event-search" accent="#F59E0B" />
              )}
              {f.title && <Input maxLength={120} placeholder="Where / when (optional)" value={f.subtitle} onChange={(e) => setF({ ...f, subtitle: e.target.value })} data-testid="top5-event-subtitle" />}
            </>
          )}
          <Input maxLength={280} placeholder="Why it's top 5 (optional)" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="top5-note-input" />
          <DialogFooter>
            <Button type="submit" disabled={!ready} className="bg-emerald-500 text-[#04120c] hover:bg-emerald-400" data-testid="top5-save-button">{extra ? "Add extra" : "Add to top 5"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
